import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { Modal, Box, IconButton, Typography, CircularProgress } from '@mui/material';
import JsonView from '@uiw/react-json-view';
import moment from 'moment';
import { logout } from '../../store/slices/authSlice';
import {
  fetchApiLogs,
  fetchBaskPatients,
  fetchBaskGhlOpportunityLogs,
  fetchBaskOrderLogs,
  fetchBaskPaymentLogs,
  fetchBaskSmsLogs,
  fetchBaskAbandonedSessionLogs,
  fetchBaskNorthbeamOrders,
  setSearchQuery,
} from '../../store/slices/dashboardSlice';
import './Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    apiLogs,
    pagination,
    loading,
    loadingLogs,
    searchQuery,
    baskPatients,
    baskPatientsPagination,
    baskGhlOpportunityLogs,
    baskGhlOpportunityLogsPagination,
    baskOrderLogs,
    baskOrderLogsPagination,
    baskPaymentLogs,
    baskPaymentLogsPagination,
    baskSmsLogs,
    baskSmsLogsPagination,
    baskAbandonedSessionLogs,
    baskAbandonedSessionLogsPagination,
    loadingBaskPatients,
    loadingBaskGhlOpportunityLogs,
    loadingBaskOrderLogs,
    loadingBaskPaymentLogs,
    loadingBaskSmsLogs,
    loadingBaskAbandonedSessionLogs,
    baskNorthbeamOrders,
    baskNorthbeamOrdersPagination,
    loadingBaskNorthbeamOrders,
  } = useSelector((state) => state.dashboard);
  const [localSearch, setLocalSearch] = useState(searchQuery || '');
  const [sectionTab, setSectionTab] = useState('api-logs');
  const [localSearchBaskPatients, setLocalSearchBaskPatients] = useState('');
  const [baskPatientTagFilter, setBaskPatientTagFilter] = useState('all');
  const [localSearchBaskOrderLogs, setLocalSearchBaskOrderLogs] = useState('');
  const [localSearchBaskOpportunityLogs, setLocalSearchBaskOpportunityLogs] =
    useState('');
  const [localSearchBaskPaymentLogs, setLocalSearchBaskPaymentLogs] =
    useState('');
  const [localSearchBaskSmsLogs, setLocalSearchBaskSmsLogs] = useState('');
  const [localSearchBaskAbandonedSessionLogs, setLocalSearchBaskAbandonedSessionLogs] =
    useState('');
  const [localSearchNorthbeam, setLocalSearchNorthbeam] = useState('');
  const [northbeamStatusFilter, setNorthbeamStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [baskPatientDetailModalOpen, setBaskPatientDetailModalOpen] =
    useState(false);
  const [selectedBaskPatient, setSelectedBaskPatient] = useState(null);
  const [baskOrderLogModalOpen, setBaskOrderLogModalOpen] = useState(false);
  const [selectedBaskOrderLog, setSelectedBaskOrderLog] = useState(null);
  const [baskOrderLogDetailTab, setBaskOrderLogDetailTab] = useState('request');
  const [baskOpportunityLogModalOpen, setBaskOpportunityLogModalOpen] =
    useState(false);
  const [selectedBaskOpportunityLog, setSelectedBaskOpportunityLog] =
    useState(null);
  const [baskPaymentLogModalOpen, setBaskPaymentLogModalOpen] = useState(false);
  const [selectedBaskPaymentLog, setSelectedBaskPaymentLog] = useState(null);
  const [activeTab, setActiveTab] = useState('request'); // 'request' or 'response' for modal
  const [baskOpportunityLogDetailTab, setBaskOpportunityLogDetailTab] =
    useState('request');
  const [baskPaymentLogDetailTab, setBaskPaymentLogDetailTab] =
    useState('request');
  const [baskSmsLogModalOpen, setBaskSmsLogModalOpen] = useState(false);
  const [selectedBaskSmsLog, setSelectedBaskSmsLog] = useState(null);
  const [baskSmsLogDetailTab, setBaskSmsLogDetailTab] = useState('message');
  const [baskAbandonedSessionLogModalOpen, setBaskAbandonedSessionLogModalOpen] =
    useState(false);
  const [selectedBaskAbandonedSessionLog, setSelectedBaskAbandonedSessionLog] =
    useState(null);
  const [baskAbandonedSessionLogDetailTab, setBaskAbandonedSessionLogDetailTab] =
    useState('request');
  const [northbeamOrderModalOpen, setNorthbeamOrderModalOpen] = useState(false);
  const [selectedNorthbeamOrder, setSelectedNorthbeamOrder] = useState(null);
  const [northbeamOrderDetailTab, setNorthbeamOrderDetailTab] =
    useState('request');
  const isInitialLoad = useRef(true);
  const debounceTimer = useRef(null);
  const isUserTyping = useRef(false);

  // Sync localSearch with Redux searchQuery (only when searchQuery changes externally, not from user input)
  useEffect(() => {
    // Only sync if searchQuery changed externally (not from our own updates)
    // This prevents infinite loops
    if (
      !isUserTyping.current &&
      searchQuery !== undefined &&
      searchQuery !== localSearch
    ) {
      setLocalSearch(searchQuery || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Initial load - only fetch on mount
  useEffect(() => {
    dispatch(fetchApiLogs({ search: '', page: 1, limit: 10 }));
  }, [dispatch]);

  // Fetch Bask patients when tab is bask-patients
  useEffect(() => {
    if (sectionTab !== 'bask-patients') return;
    dispatch(
      fetchBaskPatients({
        search: localSearchBaskPatients.trim(),
        tag: baskPatientTagFilter,
        page: 1,
        limit: 10,
      }),
    );
  }, [sectionTab, baskPatientTagFilter, dispatch]);

  // Fetch GHL opportunity logs when tab is bask-ghl-opportunity-logs
  useEffect(() => {
    if (sectionTab !== 'bask-ghl-opportunity-logs') return;
    dispatch(
      fetchBaskGhlOpportunityLogs({
        search: localSearchBaskOpportunityLogs.trim(),
        page: 1,
        limit: 10,
      }),
    );
  }, [sectionTab, dispatch]);

  // Fetch order webhook logs when tab is bask-order-logs
  useEffect(() => {
    if (sectionTab !== 'bask-order-logs') return;
    dispatch(
      fetchBaskOrderLogs({
        search: localSearchBaskOrderLogs.trim(),
        page: 1,
        limit: 10,
      }),
    );
  }, [sectionTab, dispatch]);

  // Fetch Bask payment webhook logs when tab is bask-payment-logs
  useEffect(() => {
    if (sectionTab !== 'bask-payment-logs') return;
    dispatch(
      fetchBaskPaymentLogs({
        search: localSearchBaskPaymentLogs.trim(),
        page: 1,
        limit: 10,
      }),
    );
  }, [sectionTab, dispatch]);

  // Fetch SMS logs when tab is bask-sms-logs
  useEffect(() => {
    if (sectionTab !== 'bask-sms-logs') return;
    dispatch(
      fetchBaskSmsLogs({
        search: localSearchBaskSmsLogs.trim(),
        page: 1,
        limit: 10,
      }),
    );
  }, [sectionTab, dispatch]);

  // Fetch Bask abandoned session logs when tab is active
  useEffect(() => {
    if (sectionTab !== 'bask-abandoned-session-logs') return;
    dispatch(
      fetchBaskAbandonedSessionLogs({
        search: localSearchBaskAbandonedSessionLogs.trim(),
        page: 1,
        limit: 10,
      }),
    );
  }, [sectionTab, dispatch]);

  // Fetch Northbeam data when tab is active
  useEffect(() => {
    if (sectionTab !== 'bask-northbeam-orders') return;
    dispatch(
      fetchBaskNorthbeamOrders({
        search: localSearchNorthbeam.trim(),
        page: 1,
        limit: 10,
        status: northbeamStatusFilter,
      }),
    );
  }, [sectionTab, northbeamStatusFilter, dispatch]);

  // Debounced search for Bask patients
  useEffect(() => {
    if (sectionTab !== 'bask-patients') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskPatients({
          search: localSearchBaskPatients.trim(),
          tag: baskPatientTagFilter,
          page: 1,
          limit: 10,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchBaskPatients, baskPatientTagFilter, sectionTab, dispatch]);

  // Debounced search for GHL opportunity logs
  useEffect(() => {
    if (sectionTab !== 'bask-ghl-opportunity-logs') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskGhlOpportunityLogs({
          search: localSearchBaskOpportunityLogs.trim(),
          page: 1,
          limit: 10,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchBaskOpportunityLogs, sectionTab, dispatch]);

  // Debounced search for order webhook logs
  useEffect(() => {
    if (sectionTab !== 'bask-order-logs') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskOrderLogs({
          search: localSearchBaskOrderLogs.trim(),
          page: 1,
          limit: 10,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchBaskOrderLogs, sectionTab, dispatch]);

  // Debounced search for Bask payment webhook logs
  useEffect(() => {
    if (sectionTab !== 'bask-payment-logs') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskPaymentLogs({
          search: localSearchBaskPaymentLogs.trim(),
          page: 1,
          limit: 10,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchBaskPaymentLogs, sectionTab, dispatch]);

  // Debounced search for SMS logs
  useEffect(() => {
    if (sectionTab !== 'bask-sms-logs') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskSmsLogs({
          search: localSearchBaskSmsLogs.trim(),
          page: 1,
          limit: 10,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchBaskSmsLogs, sectionTab, dispatch]);

  // Debounced search for abandoned session logs
  useEffect(() => {
    if (sectionTab !== 'bask-abandoned-session-logs') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskAbandonedSessionLogs({
          search: localSearchBaskAbandonedSessionLogs.trim(),
          page: 1,
          limit: 10,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchBaskAbandonedSessionLogs, sectionTab, dispatch]);

  // Debounced search for Northbeam orders
  useEffect(() => {
    if (sectionTab !== 'bask-northbeam-orders') return;
    const t = setTimeout(() => {
      dispatch(
        fetchBaskNorthbeamOrders({
          search: localSearchNorthbeam.trim(),
          page: 1,
          limit: 10,
          status: northbeamStatusFilter,
        }),
      );
    }, 500);
    return () => clearTimeout(t);
  }, [localSearchNorthbeam, northbeamStatusFilter, sectionTab, dispatch]);

  // Mark initial load as complete when logs are loaded
  useEffect(() => {
    if (isInitialLoad.current && !loadingLogs) {
      isInitialLoad.current = false;
    }
  }, [loadingLogs]);

  // Debounced search function
  const performSearch = useCallback(
    (searchValue) => {
      const trimmedValue = searchValue.trim();
      isUserTyping.current = false; // Reset flag before updating Redux
      dispatch(setSearchQuery(trimmedValue));
      dispatch(fetchApiLogs({ search: trimmedValue, page: 1, limit: 10 }));
    },
    [dispatch],
  );

  // Debounce effect - triggers search automatically after user stops typing
  useEffect(() => {
    // Don't search on initial mount or if still loading initial data
    if (isInitialLoad.current) {
      return;
    }

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search (500ms delay)
    debounceTimer.current = setTimeout(() => {
      performSearch(localSearch);
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearch, performSearch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    dispatch(setSearchQuery(''));
    // Clear debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    dispatch(fetchApiLogs({ search: '', page: 1, limit: 10 }));
  };

  const handlePageChange = (newPage) => {
    dispatch(fetchApiLogs({ search: searchQuery, page: newPage, limit: 10 }));
  };

  const handleBaskPatientsPageChange = (newPage) => {
    dispatch(
      fetchBaskPatients({
        search: localSearchBaskPatients.trim(),
        tag: baskPatientTagFilter,
        page: newPage,
        limit: 10,
      }),
    );
  };

  const handleBaskOrderLogsPageChange = (newPage) => {
    dispatch(
      fetchBaskOrderLogs({
        search: localSearchBaskOrderLogs.trim(),
        page: newPage,
        limit: 10,
      }),
    );
  };

  const handleOpenBaskOrderLogModal = (log) => {
    setSelectedBaskOrderLog(log);
    setBaskOrderLogModalOpen(true);
    setBaskOrderLogDetailTab('request');
  };

  const handleCloseBaskOrderLogModal = () => {
    setBaskOrderLogModalOpen(false);
    setSelectedBaskOrderLog(null);
    setBaskOrderLogDetailTab('request');
  };

  const handleBaskGhlOpportunityLogsPageChange = (newPage) => {
    dispatch(
      fetchBaskGhlOpportunityLogs({
        search: localSearchBaskOpportunityLogs.trim(),
        page: newPage,
        limit: 10,
      }),
    );
  };

  const formatOpportunityAction = (action) => {
    if (action === 'opportunity-update') return 'Update';
    if (action === 'opportunity-shipped') return 'Shipped';
    if (action === 'opportunity-create') return 'Create';
    return action || '—';
  };

  const getOpportunityActionBadgeClass = (action) => {
    if (action === 'opportunity-update') return 'updated';
    if (action === 'opportunity-shipped') return 'shipped';
    return 'created';
  };

  const getOrderLogPatientId = (log) =>
    log?.requestPayload?.data?.patientId ??
    log?.requestPayload?.patientId ??
    null;

  const getOrderLogOrderStatus = (log) =>
    log?.orderStatus ??
    log?.requestPayload?.data?.status ??
    log?.requestPayload?.data?.orderStatus ??
    log?.requestPayload?.data?.order_status ??
    log?.requestPayload?.data?.fulfillmentStatus ??
    log?.requestPayload?.data?.fulfillment_status ??
    (log?.action === 'shipped' ? 'Shipped' : null);

  const getOrderStatusBadgeClass = (orderStatus) => {
    const st = String(orderStatus || '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
    if (st === 'completed') return 'success';
    if (st === 'shipped' || st === 'senttopharmacy') return 'updated';
    if (st === 'cancelled' || st === 'canceled' || st === 'error') return 'error';
    if (st === 'pending' || st === 'abandoned') return 'created';
    return 'patient-tag-default';
  };

  const renderOrderStatusBadge = (orderStatus) => {
    if (!orderStatus) return '—';
    return (
      <span
        className={`status-badge ${getOrderStatusBadgeClass(orderStatus)}`}
      >
        {orderStatus}
      </span>
    );
  };

  const handleOpenBaskPatientDetailModal = (patient) => {
    setSelectedBaskPatient(patient);
    setBaskPatientDetailModalOpen(true);
  };

  const handleCloseBaskPatientDetailModal = () => {
    setBaskPatientDetailModalOpen(false);
    setSelectedBaskPatient(null);
  };

  const handleOpenBaskOpportunityLogModal = (log) => {
    setSelectedBaskOpportunityLog(log);
    setBaskOpportunityLogModalOpen(true);
    setBaskOpportunityLogDetailTab('request');
  };

  const handleCloseBaskOpportunityLogModal = () => {
    setBaskOpportunityLogModalOpen(false);
    setSelectedBaskOpportunityLog(null);
    setBaskOpportunityLogDetailTab('request');
  };

  const handleBaskPaymentLogsPageChange = (newPage) => {
    dispatch(
      fetchBaskPaymentLogs({
        search: localSearchBaskPaymentLogs.trim(),
        page: newPage,
        limit: 10,
      }),
    );
  };

  const handleBaskSmsLogsPageChange = (newPage) => {
    dispatch(
      fetchBaskSmsLogs({
        search: localSearchBaskSmsLogs.trim(),
        page: newPage,
        limit: 10,
      }),
    );
  };

  const handleBaskAbandonedSessionLogsPageChange = (newPage) => {
    dispatch(
      fetchBaskAbandonedSessionLogs({
        search: localSearchBaskAbandonedSessionLogs.trim(),
        page: newPage,
        limit: 10,
      }),
    );
  };

  const handleBaskNorthbeamOrdersPageChange = (newPage) => {
    dispatch(
      fetchBaskNorthbeamOrders({
        search: localSearchNorthbeam.trim(),
        page: newPage,
        limit: 10,
        status: northbeamStatusFilter,
      }),
    );
  };

  const getNorthbeamTimeOfPurchaseMs = (order) => {
    const raw = order?.timeOfPurchase ?? order?.time_of_purchase ?? null;
    if (raw == null || raw === '') return null;
    const time = new Date(raw).getTime();
    return Number.isNaN(time) ? null : time;
  };

  const sortNorthbeamOrdersByTimeOfPurchase = (rows) =>
    [...rows].sort((a, b) => {
      const aTime = getNorthbeamTimeOfPurchaseMs(a);
      const bTime = getNorthbeamTimeOfPurchaseMs(b);
      if (aTime == null && bTime == null) return 0;
      if (aTime == null) return 1;
      if (bTime == null) return -1;
      return bTime - aTime;
    });

  const renderNorthbeamTableRows = (rows) => {
    const sortedRows = sortNorthbeamOrdersByTimeOfPurchase(rows);

    if (loadingBaskNorthbeamOrders && sortedRows.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="loading-cell">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <span>Loading...</span>
            </Box>
          </td>
        </tr>
      );
    }

    if (sortedRows.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="empty-cell">
            No records found
          </td>
        </tr>
      );
    }

    return sortedRows.map((order) => (
      <tr key={`${order.syncType}-${order._id}`}>
        <td>{order.orderId || '—'}</td>
        <td>{order.customerEmail || '—'}</td>
        <td>
          {order.purchaseTotal != null
            ? `${order.currency || 'USD'} ${order.purchaseTotal}`
            : '—'}
        </td>
        <td>
          <span
            className={`action-badge ${
              order.source === 'csv' || order.syncType === 'csv'
                ? 'created'
                : 'updated'
            }`}
          >
            {order.source === 'csv' || order.syncType === 'csv'
              ? 'CSV'
              : 'Webhook'}
          </span>
        </td>
        <td>
          <span
            className={`status-badge ${
              order.status === 'success' ? 'success' : 'error'
            }`}
          >
            {order.status || '—'}
          </span>
        </td>
        <td>{order.responseStatus ?? '—'}</td>
        <td>{formatDate(order.timeOfPurchase || order.time_of_purchase)}</td>
        <td className="response-body-cell">
          <IconButton
            onClick={() => handleOpenNorthbeamOrderModal(order)}
            size="small"
            sx={{ color: '#667eea' }}
            title="View Request & Response"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </td>
      </tr>
    ));
  };

  const renderNorthbeamPagination = (pagination, onPageChange) => {
    if (pagination.pages <= 1) return null;

    return (
      <div className="pagination">
        <button
          onClick={() => onPageChange(pagination.page - 1)}
          disabled={pagination.page === 1 || loadingBaskNorthbeamOrders}
          className="page-button"
        >
          Previous
        </button>
        <span className="page-info">
          Page {pagination.page} of {pagination.pages} ({pagination.total}{' '}
          total)
        </span>
        <button
          onClick={() => onPageChange(pagination.page + 1)}
          disabled={
            pagination.page === pagination.pages || loadingBaskNorthbeamOrders
          }
          className="page-button"
        >
          Next
        </button>
      </div>
    );
  };

  const handleOpenNorthbeamOrderModal = (order) => {
    setSelectedNorthbeamOrder(order);
    setNorthbeamOrderModalOpen(true);
    setNorthbeamOrderDetailTab('request');
  };

  const handleCloseNorthbeamOrderModal = () => {
    setNorthbeamOrderModalOpen(false);
    setSelectedNorthbeamOrder(null);
    setNorthbeamOrderDetailTab('request');
  };

  const handleOpenBaskPaymentLogModal = (log) => {
    setSelectedBaskPaymentLog(log);
    setBaskPaymentLogModalOpen(true);
    setBaskPaymentLogDetailTab('request');
  };

  const handleCloseBaskPaymentLogModal = () => {
    setBaskPaymentLogModalOpen(false);
    setSelectedBaskPaymentLog(null);
  };

  const handleOpenBaskSmsLogModal = (log) => {
    setSelectedBaskSmsLog(log);
    setBaskSmsLogModalOpen(true);
    setBaskSmsLogDetailTab('message');
  };

  const handleCloseBaskSmsLogModal = () => {
    setBaskSmsLogModalOpen(false);
    setSelectedBaskSmsLog(null);
  };

  const handleOpenBaskAbandonedSessionLogModal = (log) => {
    setSelectedBaskAbandonedSessionLog(log);
    setBaskAbandonedSessionLogModalOpen(true);
    setBaskAbandonedSessionLogDetailTab('request');
  };

  const handleCloseBaskAbandonedSessionLogModal = () => {
    setBaskAbandonedSessionLogModalOpen(false);
    setSelectedBaskAbandonedSessionLog(null);
    setBaskAbandonedSessionLogDetailTab('request');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const momentDate = moment(dateString);
      if (!momentDate.isValid()) {
        return 'N/A';
      }
      return momentDate.format('MMM DD, YYYY hh:mm A');
    } catch (error) {
      return 'N/A';
    }
  };

  /** Bask payment webhooks send amount in cents; display as USD in tables. */
  const formatBaskPaymentAmountDollars = (amountCents) => {
    if (amountCents == null || amountCents === '') return '—';
    const n = Number(amountCents);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(n / 100);
  };

  const formatPaymentStatusLabel = (paymentStatus, paid) => {
    if (paymentStatus) return paymentStatus;
    if (paid === true) return 'Paid';
    if (paid === false) return 'Pending';
    return '—';
  };

  const getPaymentStatusBadgeClass = (paymentStatus, paid) => {
    const ps = String(paymentStatus || '').toLowerCase();
    if (paid === true || ps === 'paid') return 'success';
    if (ps === 'failed') return 'error';
    if (ps === 'refunded') return 'deleted';
    if (ps === 'pending' || ps === 'created') return 'created';
    return 'error';
  };

  const renderPaymentStatusBadge = (paymentStatus, paid) => (
    <span
      className={`status-badge ${getPaymentStatusBadgeClass(
        paymentStatus,
        paid,
      )}`}
    >
      {formatPaymentStatusLabel(paymentStatus, paid)}
    </span>
  );

  const formatSmsMessageType = (messageType) => {
    const labels = {
      'payment-success': 'Payment Success',
      'sent-to-pharmacy': 'Sent to Pharmacy',
      'order-shipped': 'Order Shipped',
      'order-completed-followup': 'Order Completed Follow-up',
    };
    return labels[messageType] || messageType || '—';
  };

  const getSmsStatusBadgeClass = (status) => {
    if (status === 'sent') return 'success';
    if (status === 'skipped') return 'created';
    return 'error';
  };

  const renderSmsStatusBadge = (log) => (
    <span
      className={`status-badge ${getSmsStatusBadgeClass(log?.status)}`}
      title={log?.skipReason || log?.errorMessage || ''}
    >
      {log?.status === 'sent'
        ? 'Sent'
        : log?.status === 'skipped'
          ? 'Skipped'
          : 'Failed'}
    </span>
  );

  const resolveSmsBody = (log) => {
    if (log?.body && String(log.body).trim() !== '') return log.body;
    const responseBody = log?.smsResult?.response?.body;
    if (responseBody && String(responseBody).trim() !== '') return responseBody;
    return null;
  };

  const getPatientTagBadgeClass = (tag) => {
    const t = String(tag || '').toLowerCase();
    if (t.includes('new patient')) return 'patient-tag-new';
    if (t.includes('reorder')) return 'patient-tag-reorder';
    if (t.includes('abandoned')) return 'patient-tag-abandoned';
    if (t === 'bask') return 'patient-tag-bask';
    return 'patient-tag-default';
  };

  const renderPatientTags = (tags) => {
    const list = Array.isArray(tags)
      ? [...new Set(tags.map((t) => String(t).trim()).filter(Boolean))]
      : [];
    if (!list.length) {
      return <span className="patient-tags-empty">—</span>;
    }
    return (
      <Box className="patient-tags-cell" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {list.map((tag) => (
          <span
            key={tag}
            className={`patient-tag-badge ${getPatientTagBadgeClass(tag)}`}
          >
            {tag}
          </span>
        ))}
      </Box>
    );
  };

  const handleOpenModal = (log) => {
    setSelectedLog(log);
    setModalOpen(true);
    setActiveTab('request'); // Default to request tab
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLog(null);
    setActiveTab('request');
  };

  const formatResponseBody = (body) => {
    if (!body) return null;
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return body;
      }
    }
    return body;
  };

  // Show loader if initial data is loading
  const isInitialLoading =
    isInitialLoad.current && loadingLogs;

  return (
    <div className="dashboard-container">
      <div className="dashboard-top-bar">
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </div>

      <div className="api-logs-section">
        <div className="section-tabs">
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'api-logs' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('api-logs')}
          >
            API Logs
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-patients' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-patients')}
          >
            Bask Patients
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-order-logs' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-order-logs')}
          >
            Order Logs
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-payment-logs' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-payment-logs')}
          >
            Bask Payment
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-sms-logs' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-sms-logs')}
          >
            SMS Logs
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-abandoned-session-logs' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-abandoned-session-logs')}
          >
            Abandoned Session
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-ghl-opportunity-logs' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-ghl-opportunity-logs')}
          >
            GHL Opportunity
          </button>
          <button
            type="button"
            className={`section-tab ${
              sectionTab === 'bask-northbeam-orders' ? 'active' : ''
            }`}
            onClick={() => setSectionTab('bask-northbeam-orders')}
          >
            Northbeam
          </button>
        </div>

        {sectionTab === 'api-logs' && (
          <>
            <div className="api-logs-header">
              <h2>API Logs</h2>
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Search by name, email, endpoint, or action..."
                  value={localSearch}
                  onChange={(e) => {
                    isUserTyping.current = true;
                    setLocalSearch(e.target.value);
                  }}
                  className="search-input"
                />
                {localSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Action</th>
                    <th>Date Added</th>
                    <th>Response Code</th>
                    <th>Response Body</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && apiLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : apiLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-cell">
                        No data found
                      </td>
                    </tr>
                  ) : (
                    apiLogs.map((log, index) => (
                      <tr key={log._id || index}>
                        <td>{log.name || 'N/A'}</td>
                        <td>{log.email || 'N/A'}</td>
                        <td>{log.endpoint || 'N/A'}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              log.status === 'Success' ? 'success' : 'error'
                            }`}
                          >
                            {log.status || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`action-badge ${
                              log.action === 'created'
                                ? 'created'
                                : log.action === 'deleted'
                                  ? 'deleted'
                                  : log.action === 'updated'
                                    ? 'updated'
                                    : log.action === 'recreated'
                                      ? 'recreated'
                                      : 'default'
                            }`}
                          >
                            {log.action || 'N/A'}
                          </span>
                        </td>
                        <td>{formatDate(log.dateAdded)}</td>
                        <td>{log.responseCode || 'N/A'}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() => handleOpenModal(log)}
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View Request & Response"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages || loading}
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-patients' && (
          <>
            <div className="api-logs-header">
              <h2>Bask Patients</h2>
              <div
                className="search-form"
                style={{ flexWrap: 'wrap', gap: '8px' }}
              >
                <select
                  value={baskPatientTagFilter}
                  onChange={(e) => setBaskPatientTagFilter(e.target.value)}
                  className="search-input"
                  style={{ minWidth: '160px' }}
                  aria-label="Filter by GHL tag"
                >
                  <option value="all">All tags</option>
                  <option value="new-patient">New patient</option>
                  <option value="reorder">Reorder</option>
                  <option value="abandoned-cart">Abandoned Cart</option>
                  <option value="untagged">No tags</option>
                </select>
                <input
                  type="text"
                  placeholder="Search by patient ID, name, email, phone..."
                  value={localSearchBaskPatients}
                  onChange={(e) => setLocalSearchBaskPatients(e.target.value)}
                  className="search-input"
                />
                {localSearchBaskPatients && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchBaskPatients('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Patient ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>GHL Contact ID</th>
                    <th>GHL Tags</th>
                    <th>Created At</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingBaskPatients && baskPatients.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : baskPatients.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="empty-cell">
                        No patients found
                      </td>
                    </tr>
                  ) : (
                    baskPatients.map((p) => (
                      <tr key={p._id}>
                        <td>{p.patientId || 'N/A'}</td>
                        <td>{p.patientFirstName || 'N/A'}</td>
                        <td>{p.patientLastName || 'N/A'}</td>
                        <td>{p.patientEmail || 'N/A'}</td>
                        <td>{p.phoneNumber || 'N/A'}</td>
                        <td>{p.ghlContactId || '—'}</td>
                        <td className="patient-tags-table-cell">
                          {renderPatientTags(p.ghlContactTags)}
                        </td>
                        <td>{formatDate(p.createdAt)}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() => handleOpenBaskPatientDetailModal(p)}
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View all patient details"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {baskPatientsPagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    handleBaskPatientsPageChange(
                      baskPatientsPagination.page - 1,
                    )
                  }
                  disabled={
                    baskPatientsPagination.page === 1 || loadingBaskPatients
                  }
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {baskPatientsPagination.page} of{' '}
                  {baskPatientsPagination.pages}
                </span>
                <button
                  onClick={() =>
                    handleBaskPatientsPageChange(
                      baskPatientsPagination.page + 1,
                    )
                  }
                  disabled={
                    baskPatientsPagination.page ===
                      baskPatientsPagination.pages || loadingBaskPatients
                  }
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-order-logs' && (
          <>
            <div className="api-logs-header">
              <h2>Order Logs</h2>
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Search by order ID or patient ID..."
                  value={localSearchBaskOrderLogs}
                  onChange={(e) => setLocalSearchBaskOrderLogs(e.target.value)}
                  className="search-input"
                />
                {localSearchBaskOrderLogs && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchBaskOrderLogs('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Patient ID</th>
                    <th>Webhook</th>
                    <th>Action</th>
                    <th>Order Status</th>
                    <th>Payment Status</th>
                    <th>Success</th>
                    <th>Created At</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingBaskOrderLogs && baskOrderLogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : baskOrderLogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="empty-cell">
                        No order webhook logs found
                      </td>
                    </tr>
                  ) : (
                    baskOrderLogs.map((log) => (
                      <tr key={log._id}>
                        <td>{log.orderId || '—'}</td>
                        <td>{getOrderLogPatientId(log) || '—'}</td>
                        <td>{log.webhookType || '—'}</td>
                        <td>
                          <span
                            className={`action-badge ${
                              log.action === 'update'
                                ? 'updated'
                                : log.action === 'shipped'
                                  ? 'shipped'
                                  : 'created'
                            }`}
                          >
                            {log.action === 'update'
                              ? 'Update'
                              : log.action === 'shipped'
                                ? 'Shipped'
                                : 'Create'}
                          </span>
                        </td>
                        <td>{renderOrderStatusBadge(getOrderLogOrderStatus(log))}</td>
                        <td>
                          {renderPaymentStatusBadge(log.paymentStatus, null)}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              log.success ? 'success' : 'error'
                            }`}
                          >
                            {log.success ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{formatDate(log.createdAt)}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() => handleOpenBaskOrderLogModal(log)}
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View webhook request & response"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {baskOrderLogsPagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    handleBaskOrderLogsPageChange(
                      baskOrderLogsPagination.page - 1,
                    )
                  }
                  disabled={
                    baskOrderLogsPagination.page === 1 || loadingBaskOrderLogs
                  }
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {baskOrderLogsPagination.page} of{' '}
                  {baskOrderLogsPagination.pages}
                </span>
                <button
                  onClick={() =>
                    handleBaskOrderLogsPageChange(
                      baskOrderLogsPagination.page + 1,
                    )
                  }
                  disabled={
                    baskOrderLogsPagination.page ===
                      baskOrderLogsPagination.pages || loadingBaskOrderLogs
                  }
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-ghl-opportunity-logs' && (
          <>
            <div className="api-logs-header">
              <h2>GHL Opportunity</h2>
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Search by order ID, patient ID, or GHL opportunity ID..."
                  value={localSearchBaskOpportunityLogs}
                  onChange={(e) =>
                    setLocalSearchBaskOpportunityLogs(e.target.value)
                  }
                  className="search-input"
                />
                {localSearchBaskOpportunityLogs && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchBaskOpportunityLogs('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Patient ID</th>
                    <th>GHL Opportunity ID</th>
                    <th>Action</th>
                    <th>Source</th>
                    <th>Success</th>
                    <th>Response Status</th>
                    <th>Updated At</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingBaskGhlOpportunityLogs &&
                  baskGhlOpportunityLogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : baskGhlOpportunityLogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="empty-cell">
                        No opportunity logs found
                      </td>
                    </tr>
                  ) : (
                    baskGhlOpportunityLogs.map((log) => (
                      <tr key={log._id}>
                        <td>{log.orderId || '—'}</td>
                        <td>{log.patientId || '—'}</td>
                        <td>{log.ghlOpportunityId || '—'}</td>
                        <td>
                          <span
                            className={`action-badge ${getOpportunityActionBadgeClass(
                              log.action,
                            )}`}
                          >
                            {formatOpportunityAction(log.action)}
                          </span>
                        </td>
                        <td>{log.source || '—'}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              log.success ? 'success' : 'error'
                            }`}
                          >
                            {log.success ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{log.responseStatus ?? '—'}</td>
                        <td>{formatDate(log.updatedAt || log.createdAt)}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() =>
                              handleOpenBaskOpportunityLogModal(log)
                            }
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View Request, Response & History"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {baskGhlOpportunityLogsPagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    handleBaskGhlOpportunityLogsPageChange(
                      baskGhlOpportunityLogsPagination.page - 1,
                    )
                  }
                  disabled={
                    baskGhlOpportunityLogsPagination.page === 1 ||
                    loadingBaskGhlOpportunityLogs
                  }
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {baskGhlOpportunityLogsPagination.page} of{' '}
                  {baskGhlOpportunityLogsPagination.pages}
                </span>
                <button
                  onClick={() =>
                    handleBaskGhlOpportunityLogsPageChange(
                      baskGhlOpportunityLogsPagination.page + 1,
                    )
                  }
                  disabled={
                    baskGhlOpportunityLogsPagination.page ===
                      baskGhlOpportunityLogsPagination.pages ||
                    loadingBaskGhlOpportunityLogs
                  }
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-payment-logs' && (
          <>
            <div className="api-logs-header">
              <h2>Bask Payment</h2>
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Search by patient ID or transaction ID..."
                  value={localSearchBaskPaymentLogs}
                  onChange={(e) =>
                    setLocalSearchBaskPaymentLogs(e.target.value)
                  }
                  className="search-input"
                />
                {localSearchBaskPaymentLogs && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchBaskPaymentLogs('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Patient ID</th>
                    <th>Transaction ID</th>
                    <th>Amount</th>
                    <th>Payment Status</th>
                    <th>Order Matched</th>
                    <th>Created At</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingBaskPaymentLogs && baskPaymentLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : baskPaymentLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-cell">
                        No payment logs found
                      </td>
                    </tr>
                  ) : (
                    baskPaymentLogs.map((log) => (
                      <tr key={log._id}>
                        <td>
                          <span
                            className={`action-badge ${
                              log.webhookType === 'payment-success'
                                ? 'updated'
                                : log.webhookType === 'payment-failed'
                                  ? 'deleted'
                                  : 'created'
                            }`}
                          >
                            {log.webhookType === 'payment-success'
                              ? 'Success'
                              : log.webhookType === 'payment-failed'
                                ? 'Failed'
                                : log.webhookType === 'payment-created'
                                  ? 'Created'
                                  : log.webhookType || '—'}
                          </span>
                        </td>
                        <td>{log.patientId || '—'}</td>
                        <td>{log.transactionId || '—'}</td>
                        <td>{formatBaskPaymentAmountDollars(log.amount)}</td>
                        <td>
                          {renderPaymentStatusBadge(
                            log.paymentStatus,
                            log.webhookType === 'payment-success',
                          )}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              log.orderMatched ? 'success' : 'error'
                            }`}
                          >
                            {log.orderMatched ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>{formatDate(log.createdAt)}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() => handleOpenBaskPaymentLogModal(log)}
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View Request & Response"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {baskPaymentLogsPagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    handleBaskPaymentLogsPageChange(
                      baskPaymentLogsPagination.page - 1,
                    )
                  }
                  disabled={
                    baskPaymentLogsPagination.page === 1 ||
                    loadingBaskPaymentLogs
                  }
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {baskPaymentLogsPagination.page} of{' '}
                  {baskPaymentLogsPagination.pages}
                </span>
                <button
                  onClick={() =>
                    handleBaskPaymentLogsPageChange(
                      baskPaymentLogsPagination.page + 1,
                    )
                  }
                  disabled={
                    baskPaymentLogsPagination.page ===
                      baskPaymentLogsPagination.pages || loadingBaskPaymentLogs
                  }
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-sms-logs' && (
          <>
            <div className="api-logs-header">
              <h2>SMS Logs</h2>
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Search by patient name, order ID, phone, SID, or type..."
                  value={localSearchBaskSmsLogs}
                  onChange={(e) => setLocalSearchBaskSmsLogs(e.target.value)}
                  className="search-input"
                />
                {localSearchBaskSmsLogs && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchBaskSmsLogs('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Patient Name</th>
                    <th>Order ID</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>Twilio SID</th>
                    <th>Created At</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingBaskSmsLogs && baskSmsLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : baskSmsLogs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-cell">
                        No SMS logs found
                      </td>
                    </tr>
                  ) : (
                    baskSmsLogs.map((log) => (
                      <tr key={log._id}>
                        <td>
                          <span className="action-badge updated">
                            {formatSmsMessageType(log.messageType)}
                          </span>
                        </td>
                        <td>{log.patientName || '—'}</td>
                        <td>{log.orderId || '—'}</td>
                        <td>{log.to || '—'}</td>
                        <td>{renderSmsStatusBadge(log)}</td>
                        <td>{log.twilioSid || '—'}</td>
                        <td>{formatDate(log.createdAt)}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() => handleOpenBaskSmsLogModal(log)}
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View SMS details"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {baskSmsLogsPagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    handleBaskSmsLogsPageChange(baskSmsLogsPagination.page - 1)
                  }
                  disabled={
                    baskSmsLogsPagination.page === 1 || loadingBaskSmsLogs
                  }
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {baskSmsLogsPagination.page} of{' '}
                  {baskSmsLogsPagination.pages}
                </span>
                <button
                  onClick={() =>
                    handleBaskSmsLogsPageChange(baskSmsLogsPagination.page + 1)
                  }
                  disabled={
                    baskSmsLogsPagination.page === baskSmsLogsPagination.pages ||
                    loadingBaskSmsLogs
                  }
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-abandoned-session-logs' && (
          <>
            <div className="api-logs-header">
              <h2>Abandoned Session</h2>
              <div className="search-form">
                <input
                  type="text"
                  placeholder="Search by patient ID, email, session ID..."
                  value={localSearchBaskAbandonedSessionLogs}
                  onChange={(e) =>
                    setLocalSearchBaskAbandonedSessionLogs(e.target.value)
                  }
                  className="search-input"
                />
                {localSearchBaskAbandonedSessionLogs && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchBaskAbandonedSessionLogs('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Session ID</th>
                    <th>Patient ID</th>
                    <th>Email</th>
                    <th>Checkout Complete</th>
                    <th>Created At</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingBaskAbandonedSessionLogs &&
                  baskAbandonedSessionLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="loading-cell">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <span>Loading...</span>
                        </Box>
                      </td>
                    </tr>
                  ) : baskAbandonedSessionLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-cell">
                        No abandoned session logs found
                      </td>
                    </tr>
                  ) : (
                    baskAbandonedSessionLogs.map((log) => (
                      <tr key={log._id}>
                        <td>
                          <span
                            className={`action-badge ${
                              log.action === 'add-abandoned-cart'
                                ? 'deleted'
                                : log.action === 'remove-abandoned-cart'
                                  ? 'updated'
                                  : 'default'
                            }`}
                          >
                            {log.action === 'add-abandoned-cart'
                              ? 'Add Cart Tag'
                              : log.action === 'remove-abandoned-cart'
                                ? 'Remove Cart Tag'
                                : log.action || '—'}
                          </span>
                        </td>
                        <td>{log.sessionId || '—'}</td>
                        <td>{log.patientId || '—'}</td>
                        <td>{log.patientEmail || '—'}</td>
                        <td>{log.checkoutComplete ? 'Yes' : 'No'}</td>
                        <td>{formatDate(log.createdAt)}</td>
                        <td className="response-body-cell">
                          <IconButton
                            onClick={() =>
                              handleOpenBaskAbandonedSessionLogModal(log)
                            }
                            size="small"
                            sx={{ color: '#667eea' }}
                            title="View Request & Response"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {baskAbandonedSessionLogsPagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    handleBaskAbandonedSessionLogsPageChange(
                      baskAbandonedSessionLogsPagination.page - 1,
                    )
                  }
                  disabled={
                    baskAbandonedSessionLogsPagination.page === 1 ||
                    loadingBaskAbandonedSessionLogs
                  }
                  className="page-button"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {baskAbandonedSessionLogsPagination.page} of{' '}
                  {baskAbandonedSessionLogsPagination.pages}
                </span>
                <button
                  onClick={() =>
                    handleBaskAbandonedSessionLogsPageChange(
                      baskAbandonedSessionLogsPagination.page + 1,
                    )
                  }
                  disabled={
                    baskAbandonedSessionLogsPagination.page ===
                      baskAbandonedSessionLogsPagination.pages ||
                    loadingBaskAbandonedSessionLogs
                  }
                  className="page-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {sectionTab === 'bask-northbeam-orders' && (
          <>
            <div className="api-logs-header">
              <h2>Northbeam Orders</h2>
              <div
                className="search-form"
                style={{ flexWrap: 'wrap', gap: '8px' }}
              >
                <select
                  value={northbeamStatusFilter}
                  onChange={(e) => setNorthbeamStatusFilter(e.target.value)}
                  className="search-input"
                  style={{ minWidth: '140px' }}
                >
                  <option value="all">All</option>
                  <option value="success">Stored (success)</option>
                  <option value="failed">Failed</option>
                </select>
                <input
                  type="text"
                  placeholder="Search by order ID or patient ID..."
                  value={localSearchNorthbeam}
                  onChange={(e) => setLocalSearchNorthbeam(e.target.value)}
                  className="search-input"
                />
                {localSearchNorthbeam && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchNorthbeam('')}
                    className="clear-search-button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="table-container">
              <table className="api-logs-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Email</th>
                    <th>Total</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>HTTP</th>
                    <th>Time of Purchase</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>{renderNorthbeamTableRows(baskNorthbeamOrders)}</tbody>
              </table>
            </div>
            {renderNorthbeamPagination(
              baskNorthbeamOrdersPagination,
              handleBaskNorthbeamOrdersPageChange,
            )}
          </>
        )}
      </div>

      {/* JSON View Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="json-view-modal-title"
        aria-describedby="json-view-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="json-view-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Request & Response Details
          </Typography>
          {selectedLog?.endpoint && (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {selectedLog.endpoint}
              {selectedLog.action ? ` · ${selectedLog.action}` : ''}
            </Typography>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              onClick={() => setActiveTab('request')}
              className={`tab-button ${
                activeTab === 'request' ? 'active' : ''
              }`}
            >
              GoHighLevel Request
            </button>
            <button
              onClick={() => setActiveTab('response')}
              className={`tab-button ${
                activeTab === 'response' ? 'active' : ''
              }`}
            >
              LuxSci Response
            </button>
          </Box>

          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedLog ? (
              <>
                {activeTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Request Body from GoHighLevel:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedLog.ghlRequestData ||
                          selectedLog.requestData ||
                          {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {activeTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Response from LuxSci:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedLog.luxsciResponseData ||
                          selectedLog.responseData ||
                          selectedLog.responseBody ||
                          {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Bask Patient Details Modal */}
      <Modal
        open={baskPatientDetailModalOpen}
        onClose={handleCloseBaskPatientDetailModal}
        aria-labelledby="bask-patient-detail-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-patient-detail-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Patient Details
          </Typography>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            {selectedBaskPatient ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Patient ID:</strong>{' '}
                    {selectedBaskPatient.patientId || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>First Name:</strong>{' '}
                    {selectedBaskPatient.patientFirstName || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Last Name:</strong>{' '}
                    {selectedBaskPatient.patientLastName || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong>{' '}
                    {selectedBaskPatient.patientEmail || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong>{' '}
                    {selectedBaskPatient.phoneNumber || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>GHL Contact ID:</strong>{' '}
                    {selectedBaskPatient.ghlContactId || '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" component="span">
                      <strong>GHL Tags:</strong>
                    </Typography>
                    {renderPatientTags(selectedBaskPatient.ghlContactTags)}
                  </Box>
                  <Typography variant="body2">
                    <strong>Created At:</strong>{' '}
                    {formatDate(selectedBaskPatient.createdAt)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Updated At:</strong>{' '}
                    {formatDate(selectedBaskPatient.updatedAt)}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Order Details ({selectedBaskPatient.orders?.length ?? 0})
                </Typography>
                {selectedBaskPatient.orders?.length ? (
                  <Box sx={{ mt: 1 }}>
                    {selectedBaskPatient.orders.map((order, idx) => (
                      <Box
                        key={order.orderId || idx}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          bgcolor: '#f8f9fa',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Order {idx + 1}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Order ID:</strong> {order.orderId || '—'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Order Number:</strong>{' '}
                          {order.orderNumber || '—'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Order Date:</strong>{' '}
                          {order.orderDate ? formatDate(order.orderDate) : '—'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Order Total:</strong>{' '}
                          {order.orderTotal != null ? order.orderTotal : '—'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Product:</strong> {order.productName || '—'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Status:</strong> {order.status || '—'}
                        </Typography>
                        <Typography variant="body2" component="div">
                          <strong>Payment Status:</strong>{' '}
                          {renderPaymentStatusBadge(
                            order.paymentStatus,
                            order.paid,
                          )}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Transaction ID:</strong>{' '}
                          {order.transactionId ||
                            order.paymentTransactionId ||
                            '—'}
                        </Typography>
                        <JsonView
                          value={order || {}}
                          style={{
                            backgroundColor: '#fff',
                            padding: '8px',
                            borderRadius: '4px',
                            marginTop: 8,
                            fontSize: '12px',
                          }}
                          collapsed={2}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No orders yet
                  </Typography>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Bask GHL Opportunity Log Detail Modal */}
      <Modal
        open={baskOpportunityLogModalOpen}
        onClose={handleCloseBaskOpportunityLogModal}
        aria-labelledby="bask-opportunity-log-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-opportunity-log-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            GHL Opportunity — Request, Response & History
          </Typography>
          {selectedBaskOpportunityLog && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Order: {selectedBaskOpportunityLog.orderId || '—'} · Patient:{' '}
                {selectedBaskOpportunityLog.patientId || '—'} · GHL Opportunity:{' '}
                {selectedBaskOpportunityLog.ghlOpportunityId || '—'}
              </Typography>
            </Box>
          )}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                baskOpportunityLogDetailTab === 'request' ? 'active' : ''
              }`}
              onClick={() => setBaskOpportunityLogDetailTab('request')}
            >
              Request (GHL API)
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskOpportunityLogDetailTab === 'response' ? 'active' : ''
              }`}
              onClick={() => setBaskOpportunityLogDetailTab('response')}
            >
              Response
            </button>
            {selectedBaskOpportunityLog?.history?.length > 0 && (
              <button
                type="button"
                className={`tab-button ${
                  baskOpportunityLogDetailTab === 'history' ? 'active' : ''
                }`}
                onClick={() => setBaskOpportunityLogDetailTab('history')}
              >
                History ({selectedBaskOpportunityLog.history.length})
              </button>
            )}
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedBaskOpportunityLog ? (
              <>
                {baskOpportunityLogDetailTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Latest GHL opportunity request:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskOpportunityLog.requestPayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskOpportunityLogDetailTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Latest GHL opportunity response:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskOpportunityLog.responsePayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                    {selectedBaskOpportunityLog.errorMessage && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Error: {selectedBaskOpportunityLog.errorMessage}
                      </Typography>
                    )}
                  </Box>
                )}
                {baskOpportunityLogDetailTab === 'history' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Prior sync attempts for this order:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskOpportunityLog.history || [],
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Order Webhook Log Detail Modal */}
      <Modal
        open={baskOrderLogModalOpen}
        onClose={handleCloseBaskOrderLogModal}
        aria-labelledby="bask-order-log-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-order-log-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Order Webhook — Request & Response
            {selectedBaskOrderLog?.orderId
              ? ` (Order ${selectedBaskOrderLog.orderId}${
                  getOrderLogPatientId(selectedBaskOrderLog)
                    ? ` · Patient ${getOrderLogPatientId(selectedBaskOrderLog)}`
                    : ''
                }${
                  getOrderLogOrderStatus(selectedBaskOrderLog)
                    ? ` · Status ${getOrderLogOrderStatus(selectedBaskOrderLog)}`
                    : ''
                })`
              : ''}
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                baskOrderLogDetailTab === 'request' ? 'active' : ''
              }`}
              onClick={() => setBaskOrderLogDetailTab('request')}
            >
              Request (webhook payload)
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskOrderLogDetailTab === 'response' ? 'active' : ''
              }`}
              onClick={() => setBaskOrderLogDetailTab('response')}
            >
              Response
            </button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedBaskOrderLog ? (
              <>
                {baskOrderLogDetailTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Webhook request payload:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskOrderLog.requestPayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskOrderLogDetailTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Response sent back:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskOrderLog.responsePayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                    {selectedBaskOrderLog.errorMessage && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Error: {selectedBaskOrderLog.errorMessage}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Bask Payment Webhook Log Detail Modal */}
      <Modal
        open={baskPaymentLogModalOpen}
        onClose={handleCloseBaskPaymentLogModal}
        aria-labelledby="bask-payment-log-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-payment-log-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Bask Payment Webhook — Request & Response
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                baskPaymentLogDetailTab === 'request' ? 'active' : ''
              }`}
              onClick={() => setBaskPaymentLogDetailTab('request')}
            >
              Request (webhook payload)
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskPaymentLogDetailTab === 'response' ? 'active' : ''
              }`}
              onClick={() => setBaskPaymentLogDetailTab('response')}
            >
              Response
            </button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedBaskPaymentLog ? (
              <>
                {baskPaymentLogDetailTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Webhook request payload:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskPaymentLog.requestPayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskPaymentLogDetailTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Response sent back:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskPaymentLog.responsePayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                    {selectedBaskPaymentLog.errorMessage && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Error: {selectedBaskPaymentLog.errorMessage}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* SMS Log Detail Modal */}
      <Modal
        open={baskSmsLogModalOpen}
        onClose={handleCloseBaskSmsLogModal}
        aria-labelledby="bask-sms-log-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-sms-log-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            SMS Log Details
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                baskSmsLogDetailTab === 'message' ? 'active' : ''
              }`}
              onClick={() => setBaskSmsLogDetailTab('message')}
            >
              Message
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskSmsLogDetailTab === 'response' ? 'active' : ''
              }`}
              onClick={() => setBaskSmsLogDetailTab('response')}
            >
              Twilio Response
            </button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedBaskSmsLog ? (
              <>
                {baskSmsLogDetailTab === 'message' && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Type:</strong>{' '}
                      {formatSmsMessageType(selectedBaskSmsLog.messageType)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Source:</strong> {selectedBaskSmsLog.source || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Patient ID:</strong>{' '}
                      {selectedBaskSmsLog.patientId || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order ID:</strong>{' '}
                      {selectedBaskSmsLog.orderId || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>To:</strong> {selectedBaskSmsLog.to || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>From:</strong> {selectedBaskSmsLog.from || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Status:</strong>{' '}
                      {renderSmsStatusBadge(selectedBaskSmsLog)}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}
                    >
                      Message body:
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {resolveSmsBody(selectedBaskSmsLog) || '—'}
                    </Box>
                    {selectedBaskSmsLog.skipReason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Skip reason: {selectedBaskSmsLog.skipReason}
                      </Typography>
                    )}
                    {selectedBaskSmsLog.errorMessage && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Error: {selectedBaskSmsLog.errorMessage}
                      </Typography>
                    )}
                  </Box>
                )}
                {baskSmsLogDetailTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Twilio API response:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskSmsLog.smsResult || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Bask Abandoned Session Log Detail Modal */}
      <Modal
        open={baskAbandonedSessionLogModalOpen}
        onClose={handleCloseBaskAbandonedSessionLogModal}
        aria-labelledby="bask-abandoned-session-log-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-abandoned-session-log-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Abandoned Session — Request & Response
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                baskAbandonedSessionLogDetailTab === 'request' ? 'active' : ''
              }`}
              onClick={() => setBaskAbandonedSessionLogDetailTab('request')}
            >
              Request
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskAbandonedSessionLogDetailTab === 'response' ? 'active' : ''
              }`}
              onClick={() => setBaskAbandonedSessionLogDetailTab('response')}
            >
              Response
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskAbandonedSessionLogDetailTab === 'ghl' ? 'active' : ''
              }`}
              onClick={() => setBaskAbandonedSessionLogDetailTab('ghl')}
            >
              GHL Sync
            </button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedBaskAbandonedSessionLog ? (
              <>
                {baskAbandonedSessionLogDetailTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Webhook request payload:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.requestPayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskAbandonedSessionLogDetailTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Response sent back:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.responsePayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskAbandonedSessionLogDetailTab === 'ghl' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      GHL contact tags:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.ghlContactTags || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      GHL opportunity:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.ghlOpportunity || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      GHL contact update:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.ghlContactUpdate || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Bask Abandoned Session Log Detail Modal */}
      <Modal
        open={baskAbandonedSessionLogModalOpen}
        onClose={handleCloseBaskAbandonedSessionLogModal}
        aria-labelledby="bask-abandoned-session-log-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="bask-abandoned-session-log-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Abandoned Session — Request & Response
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                baskAbandonedSessionLogDetailTab === 'request' ? 'active' : ''
              }`}
              onClick={() => setBaskAbandonedSessionLogDetailTab('request')}
            >
              Request
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskAbandonedSessionLogDetailTab === 'response' ? 'active' : ''
              }`}
              onClick={() => setBaskAbandonedSessionLogDetailTab('response')}
            >
              Response
            </button>
            <button
              type="button"
              className={`tab-button ${
                baskAbandonedSessionLogDetailTab === 'ghl' ? 'active' : ''
              }`}
              onClick={() => setBaskAbandonedSessionLogDetailTab('ghl')}
            >
              GHL Sync
            </button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedBaskAbandonedSessionLog ? (
              <>
                {baskAbandonedSessionLogDetailTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Webhook request payload:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.requestPayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskAbandonedSessionLogDetailTab === 'response' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Response sent back:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.responsePayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
                {baskAbandonedSessionLogDetailTab === 'ghl' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      GHL contact tags:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.ghlContactTags || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      GHL opportunity:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.ghlOpportunity || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      GHL contact update:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedBaskAbandonedSessionLog.ghlContactUpdate || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Northbeam Order Detail Modal */}
      <Modal
        open={northbeamOrderModalOpen}
        onClose={handleCloseNorthbeamOrderModal}
        aria-labelledby="northbeam-order-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="northbeam-order-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            Northbeam Order
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <button
              type="button"
              className={`tab-button ${
                northbeamOrderDetailTab === 'request' ? 'active' : ''
              }`}
              onClick={() => setNorthbeamOrderDetailTab('request')}
            >
              Request (Northbeam payload)
            </button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {selectedNorthbeamOrder ? (
              <>
                {northbeamOrderDetailTab === 'request' && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Payload sent to Northbeam:
                    </Typography>
                    <JsonView
                      value={formatResponseBody(
                        selectedNorthbeamOrder.requestPayload || {},
                      )}
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
};;;

// Modal style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '800px',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  maxHeight: '90vh',
  overflow: 'auto',
};

export default Dashboard;

